import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  useLazyGetQuestListPerLocationMainQuery,
  usePostFavoriteLocationMutation,
  useLazyGetOngoingQuestListPerLocationQuery,
  useLazyGetFavoriteLocationsQuery,
  useGetLocationByLatLongQuery,
  useGetUserInfoQuery,
} from "../../services/apiSlice";
import NewHeader from "./NewHeader";
import TipsQuest from "../../components/common/Tips/Tips";
import Search from "../../components/common/Field/Search";
import { useTranslation } from "react-i18next";
import QuestNearYou from "./QuestNearYou";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import { cn, getSmartLocation } from "../../Utils/helper";
import CardQuestLocation from "../../components/common/Card/CardQuestLocation";
import { useNavigate, useSearchParams } from "react-router-dom";
import images from "../../images";
import { useQuestStatusUpdater } from "../../hooks/useQuestStatusUpdater";
import UnderReview from "../UnderReview/UnderReview";
import posthog from "posthog-js";
import CardQuestPerTypeForHome from "../../components/common/Card/CardQuestPerTypeForHome";
import { usePostHog } from "../../hooks/usePostHog";

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackEvent } = usePostHog();
  const isFirstLogin = localStorage.getItem("isFirstLogin");

  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    error: null,
    loading: true,
    accuracy: null,
    source: null,
    city: null,
    country: null,
    district: null,
  });

  const [questData, setQuestData] = useState(null);
  const [questListData, setQuestListData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFavoriteLocations, setShowFavoriteLocations] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [isSearchClearing, setIsSearchClearing] = useState(false);

  const searchTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const {
    data: userInfo,
    isLoading: isUserInfoLoading,
    refetch: refetchUserInfo,
  } = useGetUserInfoQuery();

  const isAccountUnderReview = userInfo?.data?.status;
  const [
    triggerQuestList,
    { data: questResponse, isLoading: isLoadingQuestList },
  ] = useLazyGetQuestListPerLocationMainQuery();
  const [
    triggerFavoriteLocations,
    { data: favoriteLocationsResponse, isLoading: isLoadingFavoriteLocations },
  ] = useLazyGetFavoriteLocationsQuery();
  const [
    triggerOngoingQuests,
    {
      data: ongoingQuestResponse,
      error: ongoingQuestError,
      isLoading: isLoadingOngoing,
    },
  ] = useLazyGetOngoingQuestListPerLocationQuery();
  const [favorites, { isLoading: isFavoriteLoading }] =
    usePostFavoriteLocationMutation();

  const { data: locationData, isLoading: isLocationLoading } =
    useGetLocationByLatLongQuery(
      location.latitude && location.longitude
        ? { latitude: location.latitude, longitude: location.longitude }
        : undefined,
      { skip: !location.latitude || !location.longitude }
    );

  const [searchParams, setSearchParams] = useSearchParams();

  const performFetch = useCallback(
    (searchTerm = "") => {
      // Track quest discovery event
      trackEvent('quest_discovery_started', {
        has_search_query: !!searchTerm.trim(),
        search_query_length: searchTerm.trim().length,
        location_available: !!(location.latitude && location.longitude),
        location_accuracy: location.accuracy,
        location_source: location.source,
        city: location.city,
        country: location.country
      });

      triggerOngoingQuests({
        page: 1,
        limit: 10,
        search: searchTerm.trim(),
      });

      if (location.latitude && location.longitude) {
        triggerQuestList({
          latitude: location.latitude,
          longitude: location.longitude,
          limit: 10,
          search: searchTerm.trim(),
        });
      }

      // Always fetch favorite locations to keep data up-to-date
      triggerFavoriteLocations({
        page: 1,
        limit: 10,
        search: searchTerm.trim(),
      });
    },
    [
      triggerOngoingQuests,
      triggerQuestList,
      triggerFavoriteLocations,
      location.latitude,
      location.longitude,
      location.accuracy,
      location.source,
      location.city,
      location.country,
      trackEvent,
    ]
  );

  const handleQuestSaved = useCallback(
    (questId, status) => {
      performFetch(searchQuery);
    },
    [performFetch, searchQuery]
  );

  const memoizedRefetch = useCallback(() => {
    performFetch(searchQuery);
  }, [performFetch, searchQuery]);

  useQuestStatusUpdater(questListData, memoizedRefetch);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const userInfo = localStorage.getItem("userInfo");
    if (userInfo) {
      const userId = JSON.parse(userInfo)?.data?.id;
      if (userId) {
        posthog.identify(userId.toString());
      }
    }
  }, []);

  useEffect(() => {
    refetchUserInfo();
  }, [refetchUserInfo]);

  useEffect(() => {
    const searchParam = searchParams.get("search");
    if (searchParam) {
      setSearchQuery(searchParam);
    }
    setIsInitialized(true);
  }, [searchParams]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const loc = await getSmartLocation({
          gpsTimeout: 8000,
          fallbackLocation: {
            latitude: 1.3521,
            longitude: 103.8198,
          },
        });

        if (isMountedRef.current) {
          setLocation((prev) => ({
            ...prev,
            ...loc,
            loading: false,
          }));
        }
      } catch (error) {
        console.error("Location fetch error:", error);
        if (isMountedRef.current) {
          setLocation((prev) => ({
            ...prev,
            loading: false,
            error: error.message,
          }));
        }
      }
    };

    fetchLocation();
  }, []);

  useEffect(() => {
    if (locationData && !isLocationLoading && isMountedRef.current) {
      setLocation((prev) => ({
        ...prev,
        city: locationData.data.city || "",
        country: locationData.data.country || "",
        district: locationData.data.district || "",
      }));
    }
  }, [locationData, isLocationLoading]);

  useEffect(() => {
    if (
      location.latitude &&
      location.longitude &&
      !location.loading &&
      isInitialized &&
      !initialFetchDone
    ) {
      performFetch(searchQuery);
      setInitialFetchDone(true);
    }
  }, [
    location.latitude,
    location.longitude,
    location.loading,
    isInitialized,
    initialFetchDone,
    performFetch,
    searchQuery,
  ]);

  useEffect(() => {
    if (!isInitialized || !initialFetchDone) {
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        performFetch(searchQuery);
        setIsSearchClearing(false);
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, isInitialized, initialFetchDone, performFetch]);

  useEffect(() => {
    if (ongoingQuestResponse) {
      setQuestListData(ongoingQuestResponse);
      setIsSearchClearing(false);
      
      // Track ongoing quests loaded
      trackEvent('ongoing_quests_loaded', {
        quest_count: ongoingQuestResponse?.data?.length || 0,
        has_search_query: !!searchQuery.trim(),
        search_query: searchQuery.trim()
      });
    }
  }, [ongoingQuestResponse, trackEvent, searchQuery]);

  useEffect(() => {
    if (favoriteLocationsResponse) {
      setIsSearchClearing(false);
      
      // Track favorite locations loaded
      trackEvent('favorite_locations_loaded', {
        location_count: favoriteLocationsResponse?.data?.length || 0,
        has_search_query: !!searchQuery.trim()
      });
    }
  }, [favoriteLocationsResponse, trackEvent, searchQuery]);

  useEffect(() => {
    if (questResponse) {
      setQuestData(questResponse);
      
      // Track location-based quests loaded
      trackEvent('location_quests_loaded', {
        quest_count: questResponse?.data?.length || 0,
        location_latitude: location.latitude,
        location_longitude: location.longitude,
        location_city: location.city,
        has_search_query: !!searchQuery.trim(),
        location_accuracy: location.accuracy
      });
    }
  }, [questResponse, trackEvent, location, searchQuery]);

  useEffect(() => {
    if (ongoingQuestError) {
      console.error("❌ Failed to load ongoing quests:", ongoingQuestError);
    }
  }, [ongoingQuestError]);

  const handleSearchChange = useCallback(
    (query) => {
      setSearchQuery(query);

      if (query.trim()) {
        // Track search performed
        trackEvent('search_performed', {
          search_query: query.trim(),
          search_query_length: query.trim().length,
          location_city: location.city,
          location_available: !!(location.latitude && location.longitude),
          search_type: 'quest_search'
        });
        
        setSearchParams({ search: query });
        performFetch(query.trim());
      } else {
        // Track search cleared
        trackEvent('search_cleared', {
          previous_query_length: searchQuery.trim().length,
          location_city: location.city
        });
        
        setIsSearchClearing(true);
        setSearchParams({});
        performFetch("");
      }
    },
    [setSearchParams, performFetch, trackEvent, location, searchQuery]
  );

  const handleFavoriteToggle = useCallback(() => {
    setShowFavoriteLocations((prev) => !prev);
  }, []);

  const handleFavoriteLocation = async (locationId) => {
    try {
      await favorites({ location_id: locationId }).unwrap();
      performFetch(searchQuery);
    } catch (error) {
      console.error("Failed to favorite location:", error);
    }
  };

  const hasQuests = (questData?.data || []).length > 0;

  return (
    <>
      {isAccountUnderReview === "under-reviewing" ? (
        <UnderReview />
      ) : (
        <>
          <LoadingCommon
            statusLoading={
              isFavoriteLoading ||
              isLoadingOngoing ||
              isLoadingFavoriteLocations ||
              isLoadingQuestList ||
              isLocationLoading ||
              isUserInfoLoading
            }
          />
          <div className="sticky top-0 left-0 w-full bg-white z-50">
            <NewHeader className="w-full" />
          </div>
          <div className="flex flex-col mt-4 sm:mt-6 w-full gap-4 sm:gap-[30px] px-[30px] sm:px-[30px]">
            <TipsQuest isFirstLogin={isFirstLogin} />

            <Search
              className="w-full max-w-full"
              placeholder={t("home.searchLocation")}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onFavoriteToggle={handleFavoriteToggle}
              isFavorited={showFavoriteLocations}
              showFavorites={true}
            />

            {showFavoriteLocations &&
            favoriteLocationsResponse?.data?.length > 0 ? (
              <div
                className={cn(
                  favoriteLocationsResponse?.data?.length === 0
                    ? "hidden"
                    : "border-b pb-4 sm:pb-[30px] border-[#E9E6E4]"
                )}
              >
                {isLoadingFavoriteLocations || isSearchClearing ? (
                  <div className="flex justify-center items-center h-[200px]">
                    <LoadingCommon statusLoading={true} />
                    <div className="ml-4 text-gray-500 text-sm sm:text-base"></div>
                  </div>
                ) : favoriteLocationsResponse?.data?.length > 0 ? (
                  <>
                    <h5 className="text-[#253FF6] font-[Figtree] text-lg sm:text-[20px] font-bold not-italic leading-none">
                      Favourites
                    </h5>
                    <div className="flex flex-col gap-4 sm:gap-[30px] mt-4 sm:mt-[30px]">
                      {favoriteLocationsResponse.data
                        .filter((quest) => {
                          if (!searchQuery) return true;

                          const searchLower = searchQuery.toLowerCase();
                          const businessName =
                            quest.business?.company_name || "";
                          const locationAddress = quest.location?.address || "";

                          return (
                            businessName.toLowerCase().includes(searchLower) ||
                            locationAddress.toLowerCase().includes(searchLower)
                          );
                        })
                        .map((quest, index) => (
                          <CardQuestLocation
                            key={index}
                            businessName={quest.business?.company_name || ""}
                            locationId={quest.location?.id}
                            isFavorited={true}
                            onFavoriteLocation={handleFavoriteLocation}
                            businessAvatar={
                              quest.business?.avatar_data?.file_path || ""
                            }
                            address={quest.location?.name || ""}
                            handleNavigateToDetails={() =>
                              navigate(`/quest/location/${quest.location?.id}`)
                            }
                          />
                        ))}
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </div>
            ) : (
              <>
                <div
                  className={cn(
                    questListData?.data?.length === 0
                      ? "hidden"
                      : "border-b pb-4 sm:pb-[30px] border-[#E9E6E4]"
                  )}
                >
                  {isLoadingOngoing || isSearchClearing ? (
                    <div className="flex justify-center items-center h-[200px]">
                      <LoadingCommon statusLoading={true} />
                    </div>
                  ) : questListData?.data?.length > 0 ? (
                    <>
                      <h5 className="text-[#253FF6] font-[Figtree] text-lg sm:text-[20px] font-bold not-italic leading-none">
                        On-going
                      </h5>
                      {questListData.data.map((locationData) => (
                        <div
                          key={`location-${locationData.location.id}`}
                          className="flex flex-col gap-4 sm:gap-[30px] mt-4 sm:mt-[30px]"
                        >
                          <div className="flex flex-col gap-1 sm:gap-[6px]">
                            <div className="text-[#333] font-[Figtree] text-lg sm:text-[20px] font-medium not-italic leading-none">
                              {locationData?.quests[0]?.quest_business?.business
                                ?.company_name
                                ? locationData?.quests[0]?.quest_business
                                    ?.business?.company_name
                                : "Business Name"}
                            </div>

                            {locationData?.location?.name && (
                              <div className="flex flex-row gap-2 items-start">
                                <img
                                  src={images.MapPinIcon}
                                  alt="map-pin-icon"
                                  className="h-[16px] w-[16px] sm:h-[18px] sm:w-[18px]"
                                />
                                <div className="text-[#A0A0A0] font-[Figtree] text-sm sm:text-[16px] font-medium not-italic">
                                  {locationData?.location.name}
                                </div>
                              </div>
                            )}
                          </div>

                          {locationData.quests?.length > 0 ? (
                            <div className="flex flex-col gap-4 sm:gap-[30px]">
                              {locationData.quests.map((quest, questIndex) => (
                                <CardQuestPerTypeForHome
                                  key={`quest-${quest.id}-${questIndex}`}
                                  slotAvailable={quest.slot_available}
                                  consumerStatus={quest.consumer_status}
                                  questType={quest.quest_business.quest_type}
                                  rallyCoin={quest?.quest_business?.token}
                                  statusBiz={quest?.quest_business?.status_biz}
                                  participants={quest?.participants}
                                  expireTime={
                                    Array.isArray(quest.participants) &&
                                    quest.participants.length > 0
                                      ? quest.participants[0].expired_at
                                      : null
                                  }
                                  participantId={
                                    Array.isArray(quest.participants) &&
                                    quest.participants.length > 0
                                      ? quest.participants[0].id
                                      : null
                                  }
                                  questId={quest.id}
                                  onClick={() => {
                                    let navigateUrl = `/quest/details/${quest.id}`;
                                    const queryParams = new URLSearchParams();
                                    if (searchQuery) {
                                      queryParams.set("search", searchQuery);
                                    }
                                    queryParams.set("from", "home");
                                    if (queryParams.toString()) {
                                      navigateUrl += `?${queryParams.toString()}`;
                                    }
                                    navigate(navigateUrl);
                                  }}
                                  onQuestSaved={handleQuestSaved}
                                />
                              ))}
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="flex-none"></div>
                  )}
                </div>
              </>
            )}

            {hasQuests ? (
              <>
                <div className="flex flex-col gap-2 sm:gap-[12px]">
                  <h5 className="text-[#253FF6] font-[Figtree] text-lg sm:text-[20px] font-bold not-italic leading-none">
                    Quests near you
                  </h5>
                  <QuestNearYou
                    city={location.city}
                    country={location.country}
                    district={location.district}
                  />
                </div>

                {(questData?.data || [])
                  .filter((quest) => quest.business)
                  .slice(0, 10)
                  .map((quest, index) => (
                    <CardQuestLocation
                      key={index}
                      businessName={quest.business?.company_name || ""}
                      locationId={quest.location?.id}
                      isFavorited={quest.is_favorite}
                      onFavoriteLocation={handleFavoriteLocation}
                      businessAvatar={
                        quest.business?.avatar_data?.file_path || ""
                      }
                      thumnail={quest.quest_businesses}
                      address={quest.location?.name}
                      handleNavigateToDetails={() =>
                        navigate(`/quest/location/${quest.location?.id}`)
                      }
                    />
                  ))}
              </>
            ) : (
              <>
                <div className="flex flex-col justify-center items-center text-center px-4 mt-4 gap-6 w-full">
                  <div className="flex justify-center items-center">
                    <img
                      src={images.ReviewReceiptImage}
                      alt={"No quests available"}
                      className="w-[170px]"
                    />
                  </div>
                  <div className="flex flex-col justify-center font-medium text-2xl w-full text-center text-[#253FF6]">
                    <p>{t("home.wereSorry")}</p>
                  </div>
                  <div className="text-[#333333] text-lg">
                    <p>{t("home.thereAreNoQuests")}</p>
                  </div>
                  <div className="text-[#A0A0A0] text-sm">
                    <p>{t("home.pleaseTryAgainSoon")}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Home;
